// contract: design §3, re-verify at W.1
// API versioning is header-based (NestJS VersioningType.HEADER). The whop-crypto routes are
// @Version('2') on new paths; the header is sent per call, mirroring KYC_API_VERSION_V2.
export const CRYPTO_V2_API_VERSION_HEADER = 'splash-accept-version';
export const CRYPTO_V2_API_VERSION = '2';
// EDGE-32059: only the deposit-addresses GET and the new create-addresses POST move to
// @Version('3'). GET /wallets/whop-crypto/deposits (arrivals) stays @Version(V2) on the CAG —
// NestJS header-based versioning is exact-match, not a ceiling, so sending '3' there would 404
// (arrivals would look like a silently-empty list). Keep this constant separate from
// CRYPTO_V2_API_VERSION so arrivals is never accidentally bumped alongside the addresses routes.
export const CRYPTO_ADDRESSES_API_VERSION = '3';

// EDGE-31825: provisioning-cap-exhausted terminal wire status (200 { accountStatus:
// 'provisioning_failed', networks: [] }) — distinct from the transient/pollable 'provisioning'.
export const WHOP_CRYPTO_PROVISIONING_FAILED = 'provisioning_failed' as const;

export const CRYPTO_ACCOUNT_STATUSES = [
  'active',
  'suspended',
  'none',
  'provisioning',
  WHOP_CRYPTO_PROVISIONING_FAILED,
] as const;
export type CryptoAccountStatus = (typeof CRYPTO_ACCOUNT_STATUSES)[number];

// FE-internal fail-closed marker for an unrecognized wire accountStatus — never sent by the
// server. Kept OUT of CRYPTO_ACCOUNT_STATUSES (wire values only) so a schema mismatch can't
// silently normalize into it; normalizeAccountStatus assigns it explicitly on its `.otherwise`.
export type CryptoAccountStatusOrUnknown = CryptoAccountStatus | 'unknown';

export const CRYPTO_ARRIVAL_STATUSES = [
  'pending',
  'under_review',
  'completed',
  'returned',
] as const;
export type CryptoDepositArrivalStatus = (typeof CRYPTO_ARRIVAL_STATUSES)[number];

export type CryptoDepositAsset = { symbol: string; iconUrl: string | null };

export type CryptoDepositNetwork = {
  /** Network display name, e.g. "Ethereum", "Solana". */
  name: string;
  /** The deposit address. Optional (EDGE-32101): the v3 READ-ONLY addresses GET may omit it, or
   * send null/empty — the schema normalizes any of those to `undefined` and keeps the network; that
   * endpoint never discloses an address. The create POST still carries it, and the reveal reads only
   * from the POST. Never place in a URL, localStorage, or analytics payload. */
  address?: string;
  iconUrl: string | null;
  /** Assets this network's address accepts. Wire field is `supportedAssets`; the schema
   * transform renames it to `assets` here. */
  assets: CryptoDepositAsset[];
  /** EDGE-32497: the address this network disclosed before its last rotation, or null when it has
   * never rotated. Display-only ("a previous address still works") — never place in a URL,
   * localStorage, or analytics payload, same as `address`. */
  previousAddress: string | null;
  /** EDGE-32497: ISO-8601 instant of the last rotation, or null. Drives the 30-day notice in
   * CryptoAddressSheet; a malformed wire value is normalized to null by the schema. */
  addressChangedAt: string | null;
};

/** A network entry in the deposit catalog (not yet provisioned for this user). */
export type CryptoCatalogNetwork = {
  name: string;
  iconUrl: string | null;
  assets: CryptoDepositAsset[];
};

export interface ICryptoDepositAddresses {
  accountStatus: CryptoAccountStatusOrUnknown;
  networks: CryptoDepositNetwork[];
  catalog: CryptoCatalogNetwork[];
  disclosedAt: string | null;
  retryAfterAt: string | null;
}

export type CryptoDepositArrival = {
  id: string;
  status: CryptoDepositArrivalStatus;
  /**
   * DOLLARS, e.g. 23.75 — the CAG DTO converts `observed_cents` with `toDollars`
   * (API plan Slice 1, `WhopCryptoDepositItemResponseDto.observedAmount`). Never divide by 100 here.
   */
  observedAmount: number;
  currency: string;
  chain: string | null;
  txHash: string | null;
  postedAt: string;
  creditedAt: string | null;
  transactionId: string | null;
};

export interface ICryptoDepositArrivals {
  items: CryptoDepositArrival[];
  /** Opaque cursor to echo back as `since`; null when the page was empty. */
  nextSince: string | null;
}

/**
 * Thrown when POST /wallets/whop-crypto/addresses rejects with a 429
 * WHOP_CRYPTO_PROVISIONING_COOLDOWN — the backend rate-limits address-provisioning attempts.
 * `retryAfterAt` is read from `body.data.retryAfterAt`; an unparseable/missing value normalizes to
 * null. Lives alongside the other crypto-direct types (moved out of wallet.service.api.ts, which
 * is capped at one class per file — see EDGE-32059 B6 lint-conformance fix).
 */
export class CryptoCooldownError extends Error {
  constructor(public readonly retryAfterAt: string | null) {
    super('Crypto deposit address provisioning is on cooldown');
    this.name = 'CryptoCooldownError';
  }
}
